// SPDX-License-Identifier: AGPL-3.0-or-later

import {type BadgeDragItem, DragItemType} from '@app/features/app/components/layout/types/DndTypes';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {useDragAutoScroll} from '@app/features/ui/hooks/useDragAutoScroll';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {User} from '@app/features/user/models/User';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CaretLeftIcon, CaretRightIcon, EyeIcon, EyeSlashIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {
	type ReactNode,
	type RefObject,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {type ConnectableElement, DndProvider, useDrag, useDragLayer, useDrop} from 'react-dnd';
import {getEmptyImage, HTML5Backend} from 'react-dnd-html5-backend';
import {inventoryBadgePresentation} from './BadgeInventoryBadge';
import styles from './BadgeInventorySettings.module.css';
import {
	BadgeInventoryContext,
	BadgeInventoryState,
	type InventoryBadge,
	inventoryBadgeKey,
} from './BadgeInventoryState';
import {sortByBadgeOrder} from './BadgeOrder';
import {EarnableBadges} from './EarnableBadges';

const TITLE = msg({message: 'Badges', comment: 'Title of the owned badge inventory in profile settings.'});
const SHOW_NAMED = msg({
	message: 'Show {name} on profile',
	comment:
		'Accessible label of the eye switch on one owned badge tile in the badge settings. Preserve {name}, the badge name.',
});
const LOADING = msg({message: 'Loading badges…', comment: 'Status while loading the owned badge inventory.'});
const EMPTY = msg({message: 'You do not have any badges yet.', comment: 'Empty state in the owned badge inventory.'});
const LOAD_ERROR = msg({
	message: 'Could not load your badges. Try again.',
	comment: 'Error when fetching the owned badge inventory fails.',
});
const SAVE_ERROR = msg({
	message: 'Could not update badge visibility. Your previous setting was restored.',
	comment: 'Toast after an owned badge visibility update fails and is rolled back.',
});
const ORDER_SAVE_ERROR = msg({
	message: 'Could not save your badge order. Your previous order was restored.',
	comment: 'Toast after saving the order of owned badges fails and the list returns to the last saved order.',
});
const RETRY = msg({message: 'Retry', comment: 'Button retrying a failed owned badge inventory request.'});
const MOVE_UP = msg({
	message: 'Move {name} earlier',
	comment: 'Accessible label of the button moving one owned badge one place earlier. Preserve {name}, the badge name.',
});
const MOVE_DOWN = msg({
	message: 'Move {name} later',
	comment: 'Accessible label of the button moving one owned badge one place later. Preserve {name}, the badge name.',
});
const POSITION = msg({
	message: '{name}, position {position} of {count}',
	comment:
		'Screen reader announcement after moving an owned badge. Preserve {name} (badge name), {position} and {count} (numbers).',
});

type Direction = 'up' | 'down';

export const BadgeInventoryProvider = observer(
	({children, userId, enabled}: {children?: ReactNode; userId: string; enabled: boolean}) => {
		const instanceEndpoint = RuntimeConfig.apiEndpoint;
		const inventory = useMemo(() => new BadgeInventoryState(userId), [userId, instanceEndpoint]);
		const active = enabled && RuntimeConfig.features.badge_inventory_enabled === true;
		return (
			<BadgeInventoryContext.Provider value={active ? inventory : null}>{children}</BadgeInventoryContext.Provider>
		);
	},
);

const BadgeDragAutoScroll = ({listRef}: {listRef: RefObject<HTMLUListElement | null>}) => {
	const active = useDragLayer((monitor) => monitor.isDragging() && monitor.getItemType() === DragItemType.BADGE);
	const getScrollElement = useCallback(
		() => listRef.current?.closest<HTMLElement>('[data-fluxer-scroll-container="true"]') ?? null,
		[listRef],
	);
	useDragAutoScroll({active, getScrollElement});
	return null;
};

interface BadgeTileProps {
	badge: InventoryBadge;
	index: number;
	count: number;
	user: User;
	inventory: BadgeInventoryState;
	disabled?: boolean;
	canReorder: boolean;
	onHover: (dragIndex: number, hoverIndex: number) => void;
	onDragEnd: (id: string, dropped: boolean) => void;
	onArrow: (index: number, direction: Direction) => void;
}

const BadgeTile = observer(
	({badge, index, count, user, inventory, disabled, canReorder, onHover, onDragEnd, onArrow}: BadgeTileProps) => {
		const {i18n} = useLingui();
		const key = inventoryBadgeKey(badge);
		const presentation = inventoryBadgePresentation(badge, user, i18n);
		const [{isDragging}, dragRef, preview] = useDrag(
			() => ({
				type: DragItemType.BADGE,
				item: (): BadgeDragItem => ({type: DragItemType.BADGE, id: key, index}),
				canDrag: canReorder,
				collect: (monitor) => ({isDragging: monitor.isDragging()}),
				end: (item, monitor) => onDragEnd(item.id, monitor.didDrop()),
			}),
			[key, index, canReorder, onDragEnd],
		);
		const [, dropRef] = useDrop(
			() => ({
				accept: DragItemType.BADGE,
				hover: (item: BadgeDragItem) => {
					if (item.index === index) return;
					onHover(item.index, index);
					item.index = index;
				},
			}),
			[index, onHover],
		);
		useEffect(() => {
			preview(getEmptyImage(), {captureDraggingState: true});
		}, [preview]);
		const tileConnector = useCallback(
			(node: ConnectableElement | null) => {
				dragRef(node);
				dropRef(node);
			},
			[dragRef, dropRef],
		);
		const toggleDisabled = disabled || inventory.pending.has(key);
		return (
			<li
				ref={tileConnector}
				data-badge={key}
				data-reorderable={canReorder}
				className={clsx(styles.tile, isDragging && styles.dragging, canReorder && styles.reorderable)}
				data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.tile"
			>
				<Tooltip text={presentation.name} data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.tooltip">
					<div
						className={styles.badge}
						data-hidden={!badge.visible}
						data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.badge"
					>
						{presentation.iconUrl ? (
							<img
								className={styles.badgeImage}
								src={presentation.iconUrl}
								alt={presentation.name}
								draggable={false}
								data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.img"
							/>
						) : (
							<span
								className={styles.badgeText}
								role="img"
								aria-label={presentation.name}
								data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.text"
							>
								{presentation.text}
							</span>
						)}
					</div>
				</Tooltip>
				<FocusRing offset={-2} data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.focus-ring">
					<button
						type="button"
						role="switch"
						aria-checked={badge.visible}
						aria-label={i18n._(SHOW_NAMED, {name: presentation.name})}
						className={styles.visibility}
						disabled={toggleDisabled}
						onClick={() => {
							void inventory.setVisible(badge, !badge.visible).catch(() => ToastCommands.error(i18n._(SAVE_ERROR)));
						}}
						data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.visibility"
					>
						{badge.visible ? (
							<EyeIcon
								size={remFromPx(16)}
								weight="bold"
								aria-hidden="true"
								data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.eye-icon"
							/>
						) : (
							<EyeSlashIcon
								size={remFromPx(16)}
								weight="bold"
								aria-hidden="true"
								data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.eye-slash-icon"
							/>
						)}
					</button>
				</FocusRing>
				<div className={styles.arrows} data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.arrows">
					{(['up', 'down'] as const).map((direction) => (
						<FocusRing
							key={direction}
							offset={-2}
							data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.arrow-focus-ring"
						>
							<button
								type="button"
								className={styles.arrow}
								data-move={direction}
								aria-label={i18n._(direction === 'up' ? MOVE_UP : MOVE_DOWN, {name: presentation.name})}
								disabled={!canReorder || (direction === 'up' ? index === 0 : index === count - 1)}
								onClick={() => onArrow(index, direction)}
								data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.arrow"
							>
								{direction === 'up' ? (
									<CaretLeftIcon
										size={remFromPx(14)}
										weight="bold"
										aria-hidden="true"
										data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.caret-left-icon"
									/>
								) : (
									<CaretRightIcon
										size={remFromPx(14)}
										weight="bold"
										aria-hidden="true"
										data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile.caret-right-icon"
									/>
								)}
							</button>
						</FocusRing>
					))}
				</div>
			</li>
		);
	},
);

export const BadgeInventorySettings = observer(({user, disabled}: {user: User; disabled?: boolean}) => {
	const {i18n} = useLingui();
	const inventory = useContext(BadgeInventoryContext);
	const [failed, setFailed] = useState(false);
	const [dragOrder, setDragOrder] = useState<Array<string> | null>(null);
	const [announcement, setAnnouncement] = useState('');
	const dragOrderRef = useRef(dragOrder);
	dragOrderRef.current = dragOrder;
	const focusRef = useRef<{key: string; direction: Direction} | null>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const load = async () => {
		if (!inventory) return;
		setFailed(false);
		try {
			await inventory.loadAll();
		} catch {
			setFailed(true);
		}
	};
	useEffect(() => {
		setFailed(false);
		setDragOrder(null);
		if (inventory && !inventory.loaded) {
			let active = true;
			void inventory.loadAll().catch(() => {
				if (active) setFailed(true);
			});
			return () => {
				active = false;
			};
		}
		return undefined;
	}, [inventory]);
	useLayoutEffect(() => {
		const target = focusRef.current;
		if (!target) return;
		focusRef.current = null;
		const row = listRef.current?.querySelector(`[data-badge="${target.key}"]`);
		const button =
			row?.querySelector<HTMLButtonElement>(`[data-move="${target.direction}"]:not([disabled])`) ??
			row?.querySelector<HTMLButtonElement>('[data-move]:not([disabled])');
		button?.focus();
	});
	const saveFailed = useCallback(() => ToastCommands.error(i18n._(ORDER_SAVE_ERROR)), [i18n]);
	const handleHover = useCallback(
		(from: number, to: number) => {
			if (!inventory) return;
			setDragOrder((current) => {
				const keys = [...(current ?? inventory.items.map(inventoryBadgeKey))];
				const [key] = keys.splice(from, 1);
				keys.splice(to, 0, key);
				return keys;
			});
		},
		[inventory],
	);
	const handleDragEnd = useCallback(
		(id: string, dropped: boolean) => {
			const order = dragOrderRef.current;
			setDragOrder(null);
			if (!inventory || !order || !dropped) return;
			const from = inventory.items.findIndex((badge) => inventoryBadgeKey(badge) === id);
			const to = order.indexOf(id);
			if (from !== to) void inventory.move(from, to).catch(saveFailed);
		},
		[inventory, saveFailed],
	);
	if (!inventory) return null;
	const items = inventory.items;
	const shown = dragOrder ? sortByBadgeOrder(items, inventoryBadgeKey, dragOrder) : items;
	const canReorder = !disabled && inventory.loaded && inventory.nextCursor === null;
	const handleArrow = (index: number, direction: Direction) => {
		const to = direction === 'up' ? index - 1 : index + 1;
		const badge = items[index];
		focusRef.current = {key: inventoryBadgeKey(badge), direction};
		setAnnouncement(
			i18n._(POSITION, {
				name: inventoryBadgePresentation(badge, user, i18n).name,
				position: to + 1,
				count: items.length,
			}),
		);
		void inventory.move(index, to).catch(saveFailed);
	};
	return (
		<section
			className={styles.section}
			aria-label={i18n._(TITLE)}
			data-flx="user.my-profile-tab.badge-inventory-settings.section"
		>
			{inventory.loading && (
				<p role="status" data-flx="user.my-profile-tab.badge-inventory-settings.status">
					{i18n._(LOADING)}
				</p>
			)}
			{failed && (
				<div role="alert" data-flx="user.my-profile-tab.badge-inventory-settings.alert">
					{i18n._(LOAD_ERROR)}{' '}
					<Button small onClick={() => void load()} data-flx="user.my-profile-tab.badge-inventory-settings.button">
						{i18n._(RETRY)}
					</Button>
				</div>
			)}
			{inventory.loaded && items.length === 0 && (
				<p data-flx="user.my-profile-tab.badge-inventory-settings.p">{i18n._(EMPTY)}</p>
			)}
			<p role="status" className={styles.srOnly} data-flx="user.my-profile-tab.badge-inventory-settings.move-status">
				{announcement}
			</p>
			<DndProvider backend={HTML5Backend} data-flx="user.my-profile-tab.badge-inventory-settings.dnd-provider">
				<BadgeDragAutoScroll
					listRef={listRef}
					data-flx="user.my-profile-tab.badge-inventory-settings.badge-drag-auto-scroll"
				/>
				<ul ref={listRef} className={styles.grid} data-flx="user.my-profile-tab.badge-inventory-settings.grid">
					{shown.map((badge, index) => (
						<BadgeTile
							key={inventoryBadgeKey(badge)}
							badge={badge}
							index={index}
							count={shown.length}
							user={user}
							inventory={inventory}
							disabled={disabled}
							canReorder={canReorder}
							onHover={handleHover}
							onDragEnd={handleDragEnd}
							onArrow={handleArrow}
							data-flx="user.my-profile-tab.badge-inventory-settings.badge-tile"
						/>
					))}
				</ul>
			</DndProvider>
			{inventory.loaded && (
				<EarnableBadges
					user={user}
					ownedKeys={new Set(items.map(inventoryBadgeKey))}
					data-flx="user.my-profile-tab.badge-inventory-settings.earnable-badges"
				/>
			)}
		</section>
	);
});
